import {
	ORDERED_CONTRACTS_STATUSES,
	ORDERED_KYC_STATUSES,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';
import {
	AWAITING_CERTIFIER_STATUS,
	KycDisplayStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/statusStyling.helper.ts';
import { formatNumber, formatValueWithUnitDenominator } from '@dealstack/shared/lib/format/number';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	EXPECTED_STATUS,
	getDeclarationsOverallStatus,
	normalizeKycReviewStatus,
} from '@dealstack/shared/lib/common/business/kyc';
import {
	AcceptanceStatus,
	DocumentStatus,
	GetInvestmentsAllocationInfo,
	GetInvestmentsContractInfo,
	GetInvestmentsDeclarationInfo,
	GetInvestmentsExpectedFormItem,
	GetInvestmentsExpectedKycItem,
	GetInvestmentsKycInfo,
	InvestmentWorkflowType,
	KYCFileType,
	SecurityIssuedIn,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const getInvestmentType = (
	type: InvestmentWorkflowType,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	switch (type) {
		case InvestmentWorkflowType.JOINER:
		case InvestmentWorkflowType.JOINER_CREATE:
		case InvestmentWorkflowType.ALLOCATION:
			return tMipManage('investment_type.buying');
		case InvestmentWorkflowType.LEAVER:
			return tMipManage('investment_type.selling');
		default:
			return type;
	}
};

export const getTranslatedInvestmentType = (
	type: InvestmentWorkflowType,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	switch (type) {
		case InvestmentWorkflowType.JOINER_CREATE:
			return tMipManage('master_documents.text.joiner_create');
		case InvestmentWorkflowType.JOINER:
			return tMipManage('master_documents.text.joiner');
		case InvestmentWorkflowType.ALLOCATION:
			return tMipManage('master_documents.text.allocation');
		case InvestmentWorkflowType.LEAVER:
			return tMipManage('master_documents.text.leaver');
		default:
			return type;
	}
};

export const getTranslatedKycFileType = (
	type: KYCFileType,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	switch (type) {
		case KYCFileType.PROOF_OF_ADDRESS:
			return tMipManage('kyc_type.proof_of_address');
		case KYCFileType.PROOF_OF_IDENTITY:
			return tMipManage('kyc_type.proof_of_identity');
		case KYCFileType.INVESTMENT_VEHICLE:
			return tMipManage('kyc_type.investment_vehicle');
		case KYCFileType.FORM:
			return tMipManage('kyc_type.form');
		case KYCFileType.OTHER:
			return tMipManage('kyc_type.other');
		default:
			return type;
	}
};

export const getTranslationFromStatus = (
	status:
		| DocumentStatus
		| AcceptanceStatus
		| TransactionState
		| typeof EXPECTED_STATUS
		| typeof AWAITING_CERTIFIER_STATUS,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	switch (status) {
		case EXPECTED_STATUS:
			return tMipManage('kyc_status.expected');
		case AWAITING_CERTIFIER_STATUS:
			return tMipManage('kyc_status.awaiting_certifier');
		case AcceptanceStatus.REJECTED:
			return tMipManage('acceptance_status.rejected');
		case AcceptanceStatus.REREQUESTED:
			return tMipManage('acceptance_status.rerequested');
		case AcceptanceStatus.APPROVED:
			return tMipManage('acceptance_status.accepted');
		case AcceptanceStatus.PENDING:
			return tMipManage('acceptance_status.unaccepted');
		case DocumentStatus.INCOMPLETE:
			return tMipManage('documents_page.table.document_statuses.incomplete');
		case DocumentStatus.COMPLETE:
			return tMipManage('documents_page.table.document_statuses.complete');
		case DocumentStatus.COMPLETED_OUTSIDE_DEALSTACK:
			return tMipManage('documents_page.table.document_statuses.completed_outside_dealstack');
		case DocumentStatus.PENDING:
			return tMipManage('documents_page.table.document_statuses.pending');
		case DocumentStatus.NA:
			return tMipManage('documents_page.table.document_statuses.na');
		case TransactionState.COMPLETED:
			return tMipManage('payment_status.completed');
		case TransactionState.INCOMPLETE:
			return tMipManage('payment_status.incomplete');
		case TransactionState.PENDING:
			return tMipManage('payment_status.pending');
		default:
			return status;
	}
};

export const getTranslationForPaymentStatus = (
	status: TransactionState,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	switch (status) {
		case TransactionState.COMPLETED:
			return tMipManage('payment_status.completed');
		case TransactionState.INCOMPLETE:
			return tMipManage('payment_status.incomplete');
		case TransactionState.PENDING:
			return tMipManage('payment_status.pending');
		default:
			return status;
	}
};

export const getInvestmentSecuritySummary = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>,
	allocation: GetInvestmentsAllocationInfo
) => {
	const value =
		allocation.securityIssuedIn === SecurityIssuedIn.CURRENCY
			? formatNumber({
					value: allocation.cost,
					currency: allocation.currency,
					minPrecision: 2,
					maxPrecision: 2,
					returnNumericalZero: true,
				})
			: `${formatValueWithUnitDenominator(
					allocation.units ?? 0,
					allocation.securityUnitDenominator,
					true
				)} ${tCommonUi('units')}`;
	return `${allocation.securityName}: ${value}`;
};

export const getGroupedContractStatus = (contracts: GetInvestmentsContractInfo[]) => {
	const contractsByStatus = new Map<DocumentStatus, GetInvestmentsContractInfo[]>();

	contracts.forEach((item) => {
		const group = contractsByStatus.get(item.status) || [];
		group.push(item);
		contractsByStatus.set(item.status, group);
	});

	return ORDERED_CONTRACTS_STATUSES.map((status) => {
		const items = (contractsByStatus.get(status) || []).map((item) => ({
			text: item.name,
			subText: item.isSigned ? 'Signature' : '',
		}));
		return {
			status,
			items,
		};
	});
};

export const getGroupedKycDisplayStatus = (
	kyc: GetInvestmentsKycInfo[],
	expectedKyc: GetInvestmentsExpectedKycItem[],
	expectedForms: GetInvestmentsExpectedFormItem[],
	declarations: GetInvestmentsDeclarationInfo[],
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>,
	anyDeclarationRequired = true
) => {
	const itemsByStatus = new Map<KycDisplayStatus, { text: string; subText: string }[]>();

	// Initialize all statuses
	ORDERED_KYC_STATUSES.forEach((status) => itemsByStatus.set(status, []));

	// Group submitted KYC items by their status (inherited files go into EXPECTED)
	kyc.filter((item) => item.type !== KYCFileType.FORM).forEach((item) => {
		const displayStatus: KycDisplayStatus = item.isInherited
			? EXPECTED_STATUS
			: getNormalizedKycReviewStatus(item.status);
		const group = itemsByStatus.get(displayStatus) || [];
		group.push({
			text: getTranslatedKycFileType(item.type, tMipManage),
			subText: '',
		});
		itemsByStatus.set(displayStatus, group);
	});

	expectedKyc
		.filter((item) => !item.isSubmitted && item.type !== KYCFileType.FORM)
		.forEach((item) => {
			const group = itemsByStatus.get(EXPECTED_STATUS) || [];
			group.push({
				text: getTranslatedKycFileType(item.type, tMipManage),
				subText: '',
			});
			itemsByStatus.set(EXPECTED_STATUS, group);
		});

	// Add expected forms - each form template individually (inherited → EXPECTED)
	expectedForms.forEach((form) => {
		let status: KycDisplayStatus;
		if (!form.isSubmitted) {
			status = EXPECTED_STATUS;
		} else if (form.isInherited) {
			status = EXPECTED_STATUS;
		} else {
			status = form.status ?? AcceptanceStatus.PENDING;
		}
		const group = itemsByStatus.get(status) || [];
		group.push({
			text: form.formName,
			subText: '',
		});
		itemsByStatus.set(status, group);
	});

	// Add declarations as a single item (any inherited → bucket as EXPECTED)
	if (anyDeclarationRequired) {
		if (declarations.length > 0) {
			const declarationsSummary = getDeclarationsSummary(declarations, tMipManage);
			const bucket: KycDisplayStatus = declarationsSummary.hasInherited
				? EXPECTED_STATUS
				: declarationsSummary.overallStatus;
			const group = itemsByStatus.get(bucket) || [];
			group.unshift({
				text: declarationsSummary.text,
				subText: '',
			});
			itemsByStatus.set(bucket, group);
		} else {
			// If no declarations exist, show as expected
			const group = itemsByStatus.get(EXPECTED_STATUS) || [];
			group.unshift({
				text: tMipManage('kyc_type.declarations'),
				subText: '',
			});
			itemsByStatus.set(EXPECTED_STATUS, group);
		}
	}

	return ORDERED_KYC_STATUSES.map((status) => ({
		status,
		items: itemsByStatus.get(status) || [],
	}));
};

export const getDeclarationsSummary = (
	declarations: GetInvestmentsDeclarationInfo[],
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	const total = declarations.length;
	const accepted = declarations.filter((d) => d.status === AcceptanceStatus.APPROVED).length;
	const rejected = declarations.filter((d) => d.status === AcceptanceStatus.REJECTED).length;
	const unaccepted = declarations.filter(
		(d) => getNormalizedKycReviewStatus(d.status) === AcceptanceStatus.PENDING
	).length;

	const overallStatus: AcceptanceStatus = getDeclarationsOverallStatus(declarations, {
		approved: AcceptanceStatus.APPROVED,
		pending: AcceptanceStatus.PENDING,
		rejected: AcceptanceStatus.REJECTED,
	});

	return {
		total,
		accepted,
		rejected,
		unaccepted,
		overallStatus,
		text: tMipManage('kyc_type.declarations'),
		hasInherited: declarations.some((d) => d.isInherited),
	};
};

export const getNormalizedKycReviewStatus = (
	status: AcceptanceStatus | null | undefined
): AcceptanceStatus => normalizeKycReviewStatus(status, AcceptanceStatus.PENDING);

export const getStatusStat = <T extends string>(
	orderedStatuses: T[],
	statusObj: Record<T, number>
): { status: T; count: number }[] =>
	orderedStatuses.map((status) => ({
		status,
		count: statusObj[status],
	}));

export const getOptionsFromEnum = (enumType: object, labelGetter: (value: string) => string) => {
	return Object.values(enumType).map((value) => ({
		label: labelGetter(value),
		value,
	}));
};

export const getKycStatusFilterOptions = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) =>
	ORDERED_KYC_STATUSES.map((status) => ({
		label: getTranslationFromStatus(status, tMipManage),
		value: status,
	}));
